import { useNavigate, useRouterState } from "@tanstack/react-router";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

import { DatePicker } from "@/components/cinch/DatePicker";
import { Button } from "@/components/ui/button";

import {
  fmtIsoWeekRange,
  fmtMonthName,
  isoWeekNumber,
  isoWeekStart,
} from "../format";
import type { CroForecastSearch } from "../search";
import { resolveSearch } from "../search";
import { WeekLockButton } from "./WeekLockButton";

const TAB_PATHS = [
  "/cro-forecast/summary",
  "/cro-forecast/weekly-kpi",
  "/cro-forecast/forecast-meeting",
  "/cro-forecast/pipeline-movement",
  "/cro-forecast/top-of-funnel",
  "/cro-forecast/deals",
  "/cro-forecast/deal-health",
  "/cro-forecast/reconcile-deals",
  "/cro-forecast/how-to",
  "/cro-forecast/admin",
];

// The AE detail page lives under /cro-forecast/ae/$repId — match by prefix
// since the route id varies.
function isKnownCroPath(path: string): boolean {
  return (
    TAB_PATHS.includes(path) ||
    path.startsWith("/cro-forecast/ae/")
  );
}

/** Shared date selector. Lives above the SubNav so switching tabs holds the
 *  date; reads + writes the URL search params so back/forward + sharing
 *  always reproduces the view. */
export function CroForecastFilterBar({
  search,
}: {
  search: CroForecastSearch;
}) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { year, week, monthFinal } = resolveSearch(search);

  function setSearch(patch: Partial<CroForecastSearch>) {
    const next = { ...search, ...patch };
    // Drop empties so URLs stay clean.
    const stripped: CroForecastSearch = {};
    if (next.year !== undefined) stripped.year = next.year;
    if (next.week !== undefined) stripped.week = next.week;
    if (next.monthFinal) stripped.monthFinal = next.monthFinal;
    void navigate({
      to: pathname,
      search: stripped as Record<string, unknown>,
      replace: true,
    });
  }

  function shiftWeek(delta: number) {
    let nextWeek = week + delta;
    let nextYear = year;
    while (nextWeek < 1) {
      nextWeek += 52;
      nextYear -= 1;
    }
    while (nextWeek > 53) {
      nextWeek -= 52;
      nextYear += 1;
    }
    setSearch({ year: nextYear, week: nextWeek });
  }

  const isMonthMode = !!monthFinal;
  const [mfYear, mfMonth] = monthFinal
    ? monthFinal.split("-").map((s) => Number(s))
    : [year, 1];

  function shiftMonth(delta: number) {
    let m = (mfMonth ?? 1) + delta;
    let y = mfYear ?? year;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    setSearch({ monthFinal: `${y}-${String(m).padStart(2, "0")}` });
  }

  const weekStart = isoWeekStart(year, week);
  const weekRangeLabel = fmtIsoWeekRange(year, week);

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b bg-card/80 px-4 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      {!isMonthMode ? (
        <>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => shiftWeek(-1)}
            title="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-semibold tabular-nums">Wk {week}</span>
            <span className="font-mono text-xs text-muted-foreground">
              {weekRangeLabel}
            </span>
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => shiftWeek(1)}
            title="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-2 hidden text-xs text-muted-foreground sm:inline">
            Jump to date:
          </span>
          <DatePicker
            value={weekStart}
            onChange={(d) => {
              if (!d) return;
              const iso = isoWeekNumber(d);
              setSearch({ year: iso.year, week: iso.week });
            }}
            className="w-36"
          />
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs"
            onClick={() => {
              const iso = isoWeekNumber(new Date());
              setSearch({ year: iso.year, week: iso.week });
            }}
            title="Jump to the current ISO week"
          >
            This week
          </Button>
        </>
      ) : (
        <>
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => shiftMonth(-1)}
            title="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[9rem] text-center font-medium tabular-nums">
            {fmtMonthName(mfMonth ?? 1)} {mfYear} — Final
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => shiftMonth(1)}
            title="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs"
            onClick={() => {
              const now = new Date();
              const y = now.getFullYear();
              const m = String(now.getMonth() + 1).padStart(2, "0");
              setSearch({ monthFinal: `${y}-${m}` });
            }}
            title="Jump to the current month"
          >
            This month
          </Button>
        </>
      )}

      <div className="ml-auto flex items-center gap-2">
        {!isMonthMode && <WeekLockButton year={year} week={week} />}
        <Button
          size="sm"
          variant={isMonthMode ? "default" : "outline"}
          onClick={() => {
            if (isMonthMode) {
              setSearch({ monthFinal: undefined });
            } else {
              // Default to the current month.
              const now = new Date();
              const y = now.getFullYear();
              const m = String(now.getMonth() + 1).padStart(2, "0");
              setSearch({ monthFinal: `${y}-${m}` });
            }
          }}
        >
          {isMonthMode ? "Back to week view" : "Month-Final view"}
        </Button>
      </div>

      {/* Dev-only path debug; uses isKnownCroPath so AE detail (/cro-forecast/ae/$repId)
          doesn't false-alarm. import.meta.env.DEV is Vite's compile-time flag — it
          tree-shakes to nothing in prod and works in the browser (unlike
          process.env.NODE_ENV which is undefined client-side without a polyfill). */}
      {import.meta.env.DEV && !isKnownCroPath(pathname) && (
        <span className="font-mono text-[10px] text-muted-foreground/50">
          [unknown cro tab: {pathname}]
        </span>
      )}
    </div>
  );
}
